import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import AppToaster from './components/AppToaster';
import { appToastOptions } from './lib/toastOptions';
import { LayoutDashboard, ListFilter, Settings, Users, Home, Moon, Sun, LogOut, PlusCircle, Code2, Skull, Wrench, Radio, CalendarClock } from 'lucide-react';
import AdminDashboard from './components/admin/AdminDashboard';
import ForumList from './components/admin/ForumList';
import CategoryManager from './components/admin/CategoryManager';
import Settings2 from './components/admin/Settings';
import SuggestionList from './components/admin/SuggestionList';
import SoftwareManager from './components/admin/SoftwareManager';
import ForumStats from './components/ForumStats';
import ForumDetails from './components/ForumDetails';
import CategoryView from './components/CategoryView';
import CompareForums from './components/CompareForums';
import CommunityForumsPage from './components/CommunityForumsPage';
import LoginPage from './components/auth/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import SoftwareStats from './components/SoftwareStats';
import SoftwareForums from './components/SoftwareForums';
import InactiveForums from './components/InactiveForums';
import CategoriesPage from './components/CategoriesPage';
import WatchlistPage from './components/WatchlistPage';
import RisingForumsPage from './components/RisingForumsPage';
import EmbedForum from './components/EmbedForum';
import EmbedDocs from './components/EmbedDocs';
import { LanguageProvider } from './contexts/LanguageContext';
import RouteSeoGuard from './components/RouteSeoGuard';
import PublicSiteLayout from './components/PublicSiteLayout';
import AdminPageLayout from './components/admin/AdminPageLayout';
import AdminOpsHub from './components/admin/AdminOpsHub';
import ScheduleManager from './components/admin/ScheduleManager';
import AdminReportPage from './components/admin/AdminReportPage';
import LeaderboardPage from './pages/LeaderboardPage';
import TurkeyForumIndexPage from './pages/TurkeyForumIndexPage';
import SummaryPage from './pages/SummaryPage';
import HallOfFamePage from './pages/HallOfFamePage';
import AdvancedSearchPage from './pages/AdvancedSearchPage';
import CompareCategoriesPage from './pages/CompareCategoriesPage';
import TagForumsPage from './pages/TagForumsPage';
import ApiDocsPage from './pages/ApiDocsPage';
import CategoryStatsSeoPage from './pages/CategoryStatsSeoPage';
import SoftwareStatsSeoPage from './pages/SoftwareStatsSeoPage';
import SponsorPage from './pages/SponsorPage';
import AutomobilePage from './pages/AutomobilePage';
import AutomobileBrandPage from './pages/AutomobileBrandPage';
import AutomobileCatalogLayout from './components/automobile/AutomobileCatalogLayout';
import AutomobileAdminShell from './components/automobile/admin/AutomobileAdminShell';

function isAutomobilePanelPath(pathname: string): boolean {
  return pathname === '/otomobil/login' || pathname.startsWith('/otomobil/admin');
}

function AppContent() {
  const [darkMode, setDarkMode] = useState(true);
  const { user, signOut } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const location = useLocation();

  useEffect(() => {
    toast.dismiss();
  }, [location.pathname]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  const handleSignOut = async () => {
    try {
      setIsLoggingOut(true);
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  if (isLoggingOut) {
    return <Navigate to="/" replace />;
  }

  if (isAutomobilePanelPath(location.pathname)) {
    return (
      <ThemeProvider darkMode={darkMode}>
        <RouteSeoGuard />
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
          <AutomobileAdminShell
            darkMode={darkMode}
            onToggleDarkMode={() => setDarkMode(!darkMode)}
          />
          <AppToaster position="top-right" toastOptions={appToastOptions} />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider darkMode={darkMode}>
      <RouteSeoGuard />
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
        {user ? (
          <div className="flex">
            <div className="w-64 bg-white dark:bg-gray-800 shadow-sm h-screen fixed transition-colors duration-200">
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <h1 className="text-xl font-semibold text-indigo-600 dark:text-indigo-300 truncate">
                    kitapcik.net
                  </h1>
                <button
                  onClick={() => setDarkMode(!darkMode)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  {darkMode ? (
                    <Sun className="text-amber-500" size={20} />
                  ) : (
                    <Moon className="text-indigo-600" size={20} />
                  )}
                </button>
                </div>
              </div>
              <nav className="mt-4">
                <Link
                  to="/"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Home size={20} />
                  Anasayfa
                </Link>
                <Link
                  to="/software"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Code2 size={20} />
                  Yazılımlar
                </Link>
                <Link
                  to="/inactive"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Skull size={20} />
                  Forum Mezarlığı
                </Link>
                <Link
                  to="/admin"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <LayoutDashboard size={20} />
                  Dashboard
                </Link>
                <Link
                  to="/admin/forums"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Users size={20} />
                  Forumlar
                </Link>
                <Link
                  to="/admin/categories"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <ListFilter size={20} />
                  Kategoriler
                </Link>
                <Link
                  to="/admin/suggestions"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <PlusCircle size={20} />
                  Forum İstekleri
                </Link>
                <Link
                  to="/admin/software"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Wrench size={20} />
                  Yazılım Yönetimi
                </Link>
                <Link
                  to="/admin/ops"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Radio size={20} />
                  Operasyon
                </Link>
                <Link
                  to="/admin/schedules"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <CalendarClock size={20} />
                  Zamanlamalar
                </Link>
                <Link
                  to="/admin/settings"
                  className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-indigo-50 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                  <Settings size={20} />
                  Ayarlar
                </Link>
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <LogOut size={20} />
                  Çıkış Yap
                </button>
              </nav>
            </div>

            <div className="ml-64 flex-1">
              <Routes>
                <Route
                  element={
                    <PublicSiteLayout
                      darkMode={darkMode}
                      onToggleDarkMode={() => setDarkMode(!darkMode)}
                    />
                  }
                >
                  <Route path="/" element={<ForumStats darkMode={darkMode} />} />
                  <Route path="/software" element={<SoftwareStats darkMode={darkMode} />} />
                  <Route path="/software/:software" element={<SoftwareForums darkMode={darkMode} />} />
                  <Route path="/inactive" element={<InactiveForums darkMode={darkMode} />} />
                  <Route path="/category" element={<CategoriesPage darkMode={darkMode} />} />
                  <Route path="/forum/:forumId" element={<ForumDetails darkMode={darkMode} />} />
                  <Route path="/category/:categoryName" element={<CategoryView darkMode={darkMode} />} />
                  <Route path="/compare" element={<CompareForums darkMode={darkMode} />} />
                  <Route path="/watchlist" element={<WatchlistPage darkMode={darkMode} />} />
                  <Route path="/rising" element={<RisingForumsPage darkMode={darkMode} />} />
                  <Route path="/new-forums" element={<CommunityForumsPage darkMode={darkMode} />} />
                  <Route path="/add" element={<Navigate to="/new-forums?tab=suggest" replace />} />
                  <Route path="/embed-docs" element={<EmbedDocs darkMode={darkMode} />} />
                  <Route path="/leaderboard" element={<LeaderboardPage darkMode={darkMode} />} />
                  <Route path="/index" element={<TurkeyForumIndexPage darkMode={darkMode} />} />
                  <Route path="/summary/:period" element={<SummaryPage darkMode={darkMode} />} />
                  <Route path="/hall-of-fame" element={<HallOfFamePage darkMode={darkMode} />} />
                  <Route path="/search" element={<AdvancedSearchPage darkMode={darkMode} />} />
                  <Route path="/compare-categories" element={<CompareCategoriesPage darkMode={darkMode} />} />
                  <Route path="/tag/:tag" element={<TagForumsPage darkMode={darkMode} />} />
                  <Route path="/api-docs" element={<ApiDocsPage darkMode={darkMode} />} />
                  <Route path="/kategori/:slug/istatistikler" element={<CategoryStatsSeoPage darkMode={darkMode} />} />
                  <Route path="/yazilim/:slug/istatistikleri" element={<SoftwareStatsSeoPage darkMode={darkMode} />} />
                  <Route path="/sponsor" element={<SponsorPage darkMode={darkMode} />} />
                  <Route path="/otomobil" element={<AutomobileCatalogLayout />}>
                    <Route index element={<AutomobilePage darkMode={darkMode} />} />
                    <Route path=":slug" element={<AutomobileBrandPage darkMode={darkMode} />} />
                  </Route>
                </Route>
                <Route path="/embed/:forumId" element={<EmbedForum />} />
                <Route element={<AdminPageLayout />}>
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/forums"
                    element={
                      <ProtectedRoute>
                        <ForumList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <ProtectedRoute>
                        <CategoryManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/suggestions"
                    element={
                      <ProtectedRoute>
                        <SuggestionList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute>
                        <Settings2 />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/software"
                    element={
                      <ProtectedRoute>
                        <SoftwareManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/admin/automobile" element={<Navigate to="/otomobil/admin" replace />} />
                  <Route path="/admin/ops"
                    element={
                      <ProtectedRoute>
                        <AdminOpsHub />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/schedules"
                    element={
                      <ProtectedRoute>
                        <ScheduleManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/report"
                    element={
                      <ProtectedRoute>
                        <AdminReportPage />
                      </ProtectedRoute>
                    }
                  />
                </Route>
              </Routes>
            </div>
          </div>
        ) : (
          <Routes>
            <Route
              element={
                <PublicSiteLayout
                  darkMode={darkMode}
                  onToggleDarkMode={() => setDarkMode(!darkMode)}
                />
              }
            >
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/automobile" element={<Navigate to="/otomobil/admin" replace />} />
              <Route path="/" element={<ForumStats darkMode={darkMode} />} />
              <Route path="/software" element={<SoftwareStats darkMode={darkMode} />} />
              <Route path="/software/:software" element={<SoftwareForums darkMode={darkMode} />} />
              <Route path="/inactive" element={<InactiveForums darkMode={darkMode} />} />
              <Route path="/category" element={<CategoriesPage darkMode={darkMode} />} />
              <Route path="/forum/:forumId" element={<ForumDetails darkMode={darkMode} />} />
              <Route path="/category/:categoryName" element={<CategoryView darkMode={darkMode} />} />
              <Route path="/compare" element={<CompareForums darkMode={darkMode} />} />
              <Route path="/watchlist" element={<WatchlistPage darkMode={darkMode} />} />
              <Route path="/rising" element={<RisingForumsPage darkMode={darkMode} />} />
              <Route path="/new-forums" element={<CommunityForumsPage darkMode={darkMode} />} />
              <Route path="/add" element={<Navigate to="/new-forums?tab=suggest" replace />} />
              <Route path="/embed-docs" element={<EmbedDocs darkMode={darkMode} />} />
              <Route path="/leaderboard" element={<LeaderboardPage darkMode={darkMode} />} />
              <Route path="/index" element={<TurkeyForumIndexPage darkMode={darkMode} />} />
              <Route path="/summary/:period" element={<SummaryPage darkMode={darkMode} />} />
              <Route path="/hall-of-fame" element={<HallOfFamePage darkMode={darkMode} />} />
              <Route path="/search" element={<AdvancedSearchPage darkMode={darkMode} />} />
              <Route path="/compare-categories" element={<CompareCategoriesPage darkMode={darkMode} />} />
              <Route path="/tag/:tag" element={<TagForumsPage darkMode={darkMode} />} />
              <Route path="/api-docs" element={<ApiDocsPage darkMode={darkMode} />} />
              <Route path="/kategori/:slug/istatistikler" element={<CategoryStatsSeoPage darkMode={darkMode} />} />
              <Route path="/yazilim/:slug/istatistikleri" element={<SoftwareStatsSeoPage darkMode={darkMode} />} />
              <Route path="/sponsor" element={<SponsorPage darkMode={darkMode} />} />
              <Route path="/otomobil" element={<AutomobileCatalogLayout />}>
                <Route index element={<AutomobilePage darkMode={darkMode} />} />
                <Route path=":slug" element={<AutomobileBrandPage darkMode={darkMode} />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
            <Route path="/embed/:forumId" element={<EmbedForum />} />
          </Routes>
        )}

        <AppToaster position="top-right" toastOptions={appToastOptions} />
      </div>
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;